import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, Lock, Truck, Zap, Wallet, Gift } from "lucide-react";
import { API, img } from "@/lib/format";
import { useStore } from "@/context/StoreContext";
import { GoogleMark } from "@/components/GoogleMark";
import useSEO from "@/hooks/useSEO";

const loadRazorpay = () => new Promise((resolve) => {
  if (window.Razorpay) return resolve(true);
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = () => resolve(true);
  s.onerror = () => resolve(false);
  document.body.appendChild(s);
});

export default function Checkout() {
  const navigate = useNavigate();
  const { cart, subtotalInr, subtotalUsd, discountInr, discountUsd, promo,
    fmt, config, clearCart, user, wallet } = useStore();
  const [form, setForm] = useState({ name: "", email: "", phone: "", address1: "", city: "", state: "", pincode: "", country: "India" });
  const [shippingMethod, setShippingMethod] = useState("standard");
  const [placing, setPlacing] = useState(false);
  const [recovering, setRecovering] = useState(() => {
    try { return JSON.parse(localStorage.getItem("jk_pending_order") || "null"); } catch { return null; }
  });
  const [payFailed, setPayFailed] = useState(null);
  const [useWallet, setUseWallet] = useState(true);
  const pollRef = useRef(null);

  const confirmPaid = async (orderId, email, payload) => {
    await axios.post(`${API}/payments/razorpay/verify`, payload).catch(() => {});
    localStorage.removeItem("jk_pending_order");
    clearCart();
    navigate(`/order-confirmation/${orderId}?c=${encodeURIComponent(email)}`);
  };

  const startPolling = (orderId, email) => {
    if (pollRef.current) clearInterval(pollRef.current);
    let tries = 0;
    pollRef.current = setInterval(async () => {
      tries += 1;
      if (tries > 60) { clearInterval(pollRef.current); return; }
      try {
        const { data } = await axios.get(`${API}/payments/razorpay/status/${orderId}`);
        if (data.payment_status === "paid") {
          clearInterval(pollRef.current);
          localStorage.removeItem("jk_pending_order");
          toast.success("Payment received — order confirmed!");
          clearCart();
          navigate(`/order-confirmation/${orderId}?c=${encodeURIComponent(email)}`);
        } else if (data.last_payment_status === "failed") {
          clearInterval(pollRef.current);
          localStorage.removeItem("jk_pending_order");
          setRecovering(null);
          setPayFailed(data.failure_reason || "Your payment didn't go through");
          toast.error("Payment didn't go through — no money was charged");
        }
      } catch { /* keep polling */ }
    }, 4000);
  };

  useEffect(() => {
    if (recovering) {
      startPolling(recovering.order_id, recovering.email);
      return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }
  }, []);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, name: f.name || user.name || "", email: f.email || user.email || "" }));
  }, [user]);

  useSEO({ title: "Checkout — Just Kords", description: "Secure checkout with Razorpay — UPI, cards, NetBanking and wallets." });

  const threshold = config.free_shipping_threshold || 2999;
  const shippingFee = shippingMethod === "express" ? (config.express_fee || 199) : (subtotalInr >= threshold ? 0 : config.shipping_fee || 99);
  const walletApply = wallet && wallet.balance > 0 && useWallet
    ? Math.min(wallet.balance, Math.max(subtotalInr - discountInr, 0)) : 0;
  const totalInr = subtotalInr - discountInr - walletApply + shippingFee;
  const cashbackEarn = Math.round(subtotalInr * (config.cashback_rate || 0.10));

  if (recovering && cart.length === 0 && !placing) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center gap-5 bg-alabaster px-4 pt-28 text-center" data-testid="checkout-recovery">
        <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-crimson">Hold on</p>
        <h1 className="font-serif text-4xl font-light">Confirming your payment…</h1>
        {payFailed ? (
          <div data-testid="recovery-failed" className="max-w-md bg-crimson-sub px-6 py-5 text-left">
            <p className="text-sm font-semibold text-crimson">This payment didn't go through — no money was charged.</p>
            <p className="mt-2 text-xs leading-relaxed text-mutedc">Reason: {payFailed}. You can place the order again — trying a different UPI app or paying by card usually works.</p>
          </div>
        ) : (
          <p className="max-w-md text-sm leading-relaxed text-mutedc">
            Order <span className="font-mono font-semibold text-ink">{recovering.order_id}</span> — we're checking with Razorpay whether your payment landed. This page will jump to your confirmation automatically.
          </p>
        )}
        <Link to="/shop" data-testid="recovery-shop-link" className="rounded-full bg-crimson px-8 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-crimson-dark">
          Try Again — Back to Shop
        </Link>
      </div>
    );
  }

  if (cart.length === 0 && !placing) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center gap-5 bg-alabaster px-4 pt-28 text-center" data-testid="checkout-empty">
        {payFailed ? (
          <>
            <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-crimson">Payment Not Received</p>
            <p className="font-serif text-4xl font-light">That payment didn't go through.</p>
            <p className="max-w-md text-sm leading-relaxed text-mutedc" data-testid="checkout-failed-message">
              No money was charged. Reason: {payFailed}. You can place the order again — a different UPI app or paying by card usually works.
            </p>
          </>
        ) : (
          <p className="font-serif text-4xl">Nothing to check out yet.</p>
        )}
        <Link to="/shop" className="rounded-full bg-crimson px-8 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-crimson-dark">
          {payFailed ? "Try Again — Shop the Collection" : "Shop the Collection"}
        </Link>
      </div>
    );
  }

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const signInFromCheckout = () => {
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/checkout")}`;
  };

  const validate = () => {
    const missing = Object.entries(form).filter(([k, v]) => k !== "country" && !v.trim());
    if (missing.length) { toast.error("Please fill in every field"); return false; }
    if (!form.email.includes("@")) { toast.error("That email doesn't look right"); return false; }
    if (form.phone.replace(/\D/g, "").length < 10) { toast.error("Phone number needs at least 10 digits"); return false; }
    return true;
  };

  const placeOrder = async () => {
    if (!validate()) return;
    setPlacing(true);
    try {
      const { data: order } = await axios.post(`${API}/orders`, {
        items: cart.map((i) => ({ slug: i.slug, size: i.size, qty: i.qty })),
        ...form, shipping_method: shippingMethod, payment_method: "razorpay", currency: fmtCurrency(),
        promo_code: promo || "", use_wallet: walletApply > 0,
      });

      const { data: pay } = await axios.post(`${API}/payments/razorpay/create`, { order_id: order.order_id });
      localStorage.setItem("jk_pending_order", JSON.stringify({ order_id: order.order_id, email: form.email }));

      if (pay.demo) {
        await confirmPaid(order.order_id, form.email, { order_id: order.order_id, demo: true });
        return;
      }

      const ok = await loadRazorpay();
      if (!ok) { toast.error("Couldn't reach Razorpay. Try again."); setPlacing(false); return; }

      const rzp = new window.Razorpay({
        key: pay.key_id,
        amount: pay.amount,
        currency: "INR",
        name: "Just Kords",
        description: `Order ${order.order_id}`,
        order_id: pay.razorpay_order_id,
        prefill: { name: form.name, email: form.email, contact: form.phone },
        theme: { color: "#C8102E" },
        handler: (res) => {
          confirmPaid(order.order_id, form.email, {
            order_id: order.order_id,
            razorpay_order_id: res.razorpay_order_id,
            razorpay_payment_id: res.razorpay_payment_id,
            razorpay_signature: res.razorpay_signature,
          });
        },
        modal: {
          ondismiss: () => {
            toast.info("If you already paid, we're confirming it now — hang tight.");
            setPlacing(false);
            startPolling(order.order_id, form.email);
          },
        },
      });
      rzp.on("payment.failed", () => { toast.error("Payment failed. Please try again."); setPlacing(false); });
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't place the order. Please try again.");
      setPlacing(false);
    }
  };

  const fmtCurrency = () => (localStorage.getItem("jk_currency") || "INR");

  return (
    <div className="bg-alabaster pt-28 lg:pt-36">
      <div className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
        <h1 className="border-b border-linec pb-8 font-serif text-5xl font-light" data-testid="checkout-title">Checkout</h1>
        {user === null && (
          <div className="mt-8 flex flex-wrap items-center justify-between gap-6 bg-crimson p-7 text-white" data-testid="checkout-cashback-signin-banner">
            <div className="flex items-start gap-4">
              <Gift size={30} className="mt-1.5 shrink-0" />
              <div>
                <p className="font-serif text-2xl leading-snug" data-testid="checkout-cashback-signin-title">
                  Sign in through ur Google account and get <strong>10% Site Wallet Cashback</strong> on this order
                </p>
                <p className="mt-2 text-sm text-white/85">
                  Do u love Cashbacks, each time u shop with JustKords? Then, sign in now!!
                </p>
              </div>
            </div>
            <button
              data-testid="checkout-cashback-signin-button" onClick={signInFromCheckout}
              className="flex w-full items-center justify-center gap-3 rounded-full bg-white px-7 py-4 text-sm font-semibold text-ink shadow-lg transition-transform duration-200 hover:scale-[1.03] sm:w-auto"
            >
              <GoogleMark /> Sign in with Google
            </button>
          </div>
        )}
        {payFailed && (
          <div className="mt-6 bg-crimson-sub px-5 py-4 text-sm text-crimson" data-testid="checkout-failed-banner">
            <span className="font-semibold">Your last payment didn't go through — no money was charged.</span> Reason: {payFailed}. You can safely try again — a different UPI app or card usually works.
          </div>
        )}
        {recovering && (
          <p className="mt-6 bg-crimson-sub px-5 py-4 text-sm text-crimson" data-testid="checkout-recovery-banner">
            You have a pending payment for order <span className="font-mono font-semibold">{recovering.order_id}</span> — we're confirming it in the background and you'll be redirected the moment it clears.
          </p>
        )}
        <div className="grid gap-14 py-10 lg:grid-cols-5">
          <div className="space-y-10 lg:col-span-3">
            <section data-testid="checkout-contact-section">
              <h2 className="font-mono text-[11px] uppercase tracking-[0.3em] text-crimson">1 · Contact & Delivery</h2>
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                {[["name", "Full name", "sm:col-span-2"], ["email", "Email", ""], ["phone", "Phone", ""], ["address1", "Address — house, street, area", "sm:col-span-2"], ["city", "City", ""], ["state", "State", ""], ["pincode", "PIN code", ""], ["country", "Country", ""]].map(([k, ph, extra]) => (
                  <label key={k} className={`block ${extra}`}>
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mutedc">{ph}</span>
                    <input
                      data-testid={`checkout-${k}-input`}
                      value={form[k]} onChange={set(k)}
                      className="mt-1.5 w-full border border-linec bg-white px-4 py-3 text-sm focus:border-crimson focus:outline-none"
                    />
                  </label>
                ))}
              </div>
            </section>

            <section data-testid="checkout-shipping-section">
              <h2 className="font-mono text-[11px] uppercase tracking-[0.3em] text-crimson">2 · Shipping Method</h2>
              <div className="mt-5 space-y-3">
                {[
                  ["standard", Truck, "Standard", subtotalInr >= threshold ? "Free" : fmt(config.shipping_fee || 99), "4-7 days"],
                  ["express", Zap, "Express", fmt(config.express_fee || 199), "2-3 days"],
                ].map(([id, Icon, label, price, time]) => (
                  <button
                    key={id} data-testid={`checkout-shipping-${id}`} onClick={() => setShippingMethod(id)}
                    className={`flex w-full items-center justify-between border p-5 text-left transition-colors duration-200 ${shippingMethod === id ? "border-crimson bg-crimson-sub" : "border-linec bg-white hover:border-ink"}`}
                  >
                    <span className="flex items-center gap-4">
                      <Icon size={19} className="text-crimson" />
                      <span>
                        <span className="block text-sm font-semibold">{label}</span>
                        <span className="block text-xs text-mutedc">{time}</span>
                      </span>
                    </span>
                    <span className="text-sm font-semibold">{price}</span>
                  </button>
                ))}
              </div>
            </section>

            <section data-testid="checkout-payment-section">
              <h2 className="font-mono text-[11px] uppercase tracking-[0.3em] text-crimson">3 · Payment</h2>
              <div className="mt-5 flex items-center justify-between border border-crimson bg-crimson-sub p-5" data-testid="checkout-payment-razorpay">
                <span className="flex items-center gap-4">
                  <ShieldCheck size={19} className="text-crimson" />
                  <span>
                    <span className="block text-sm font-semibold">UPI, Cards, NetBanking &amp; Wallets</span>
                    <span className="block text-xs text-mutedc">100% secure — powered by Razorpay</span>
                  </span>
                </span>
                <span className="font-mono text-[10px] uppercase tracking-widest text-crimson">Selected</span>
              </div>
            </section>
          </div>

          <div className="lg:col-span-2">
            <div className="sticky top-36 border border-linec bg-white p-7" data-testid="checkout-summary">
              <h2 className="font-serif text-2xl">Order Summary</h2>
              {wallet && wallet.balance > 0 && (
                <div className="mt-4 border border-crimson bg-crimson-sub p-4" data-testid="checkout-wallet-banner">
                  <label className="flex cursor-pointer items-center justify-between gap-3">
                    <span className="flex items-center gap-2 text-sm font-semibold text-ink">
                      <Wallet size={15} className="text-crimson" />
                      Use {fmt(wallet.balance)} wallet cashback
                    </span>
                    <input type="checkbox" data-testid="checkout-wallet-toggle" checked={useWallet}
                      onChange={(e) => setUseWallet(e.target.checked)} className="h-4 w-4 accent-crimson" />
                  </label>
                  <p className="mt-2 text-[10px] leading-relaxed text-mutedc">
                    10% of every purchase comes back as cashback · valid 90 days · non-transferable · no cash exchange · calculated on product value, excluding shipping.
                  </p>
                </div>
              )}
              <div className="mt-5 max-h-56 space-y-4 overflow-y-auto pr-1">
                {cart.map((i) => (
                  <div key={`${i.slug}:${i.size}`} className="flex items-center gap-3" data-testid={`checkout-item-${i.slug}`}>
                    <div className="relative">
                      {i.image
                        ? <img src={img(i.image, 120)} alt={i.name} className="h-16 w-14 object-cover" />
                        : <div className="h-16 w-14 bg-linec" />}
                      <span className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-ink font-mono text-[10px] text-white">{i.qty}</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-semibold leading-snug">{i.name}</p>
                      <p className="font-mono text-[10px] uppercase tracking-widest text-mutedc">Size {i.size}</p>
                    </div>
                    <p className="text-xs font-semibold">{fmt(i.price_inr * i.qty, i.price_usd * i.qty)}</p>
                  </div>
                ))}
              </div>
              <div className="mt-6 space-y-2.5 border-t border-linec pt-5 text-sm">
                <div className="flex justify-between"><span className="text-mutedc">Subtotal</span><span>{fmt(subtotalInr, subtotalUsd)}</span></div>
                {promo && <div className="flex justify-between text-crimson" data-testid="checkout-discount"><span>JUST10</span><span>−{fmt(discountInr, discountUsd)}</span></div>}
                {walletApply > 0 && <div className="flex justify-between text-crimson" data-testid="checkout-wallet-discount"><span>Wallet cashback</span><span>−{fmt(walletApply)}</span></div>}
                <div className="flex justify-between"><span className="text-mutedc">Shipping</span><span data-testid="checkout-shipping-fee">{shippingFee === 0 ? "Free" : fmt(shippingFee, (config.express_fee || 199) / (config.usd_rate || 83))}</span></div>
                <div className="flex justify-between border-t border-linec pt-3">
                  <span className="font-semibold">Total</span>
                  <span data-testid="checkout-total" className="font-serif text-2xl">{fmt(totalInr)}</span>
                </div>
                {user && (
                  <p className="text-xs text-mutedc" data-testid="checkout-cashback-preview">
                    You'll earn {fmt(cashbackEarn)} cashback on this order
                  </p>
                )}
              </div>
              <button
                data-testid="checkout-place-order" onClick={placeOrder} disabled={placing}
                className="mt-6 w-full rounded-full bg-crimson py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-crimson-dark disabled:opacity-60"
              >
                {placing ? "Placing order…" : `Pay ${fmt(totalInr)} Securely`}
              </button>
              <div className="mt-4 flex items-center justify-center gap-2 text-mutedc" data-testid="checkout-secure-note">
                <Lock size={12} /><span className="font-mono text-[10px] uppercase tracking-widest">256-bit SSL · Razorpay certified</span>
                <ShieldCheck size={12} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
